import { useState } from 'react';
import { ExternalLink, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mobileApps, appCategories } from '@/data/portfolio';
import {
  SiGoogleplay, SiAppstore
} from 'react-icons/si';
import { TiltCard } from '@/components/ui/TiltCard';

// Category icon colors for visual distinction
const categoryColors: Record<string, string> = {
  "On-Demand Services": "from-orange-500 to-amber-500",
  "Fitness & Sports": "from-green-500 to-emerald-500",
  "Navigation": "from-blue-500 to-cyan-500",
  "Industrial": "from-slate-600 to-slate-700",
  "Travel & Tourism": "from-purple-500 to-pink-500",
  "Parking & Transport": "from-indigo-500 to-blue-500",
  "Fitness": "from-lime-500 to-green-500",
  "Health & Wellness": "from-rose-500 to-pink-500",
  "Business Tools": "from-purple-600 to-indigo-600",
  "Lifestyle": "from-orange-500 to-amber-500",
  "Food & Dining": "from-red-500 to-orange-500",
  "Sports": "from-teal-500 to-cyan-500",
  "Education": "from-yellow-500 to-amber-500",
  "Finance & Crypto": "from-violet-500 to-purple-500",
  "Healthcare": "from-blue-600 to-cyan-600",
  "Safety": "from-red-600 to-rose-600",
  "Photo & Video": "from-fuchsia-500 to-pink-500",
  "Business": "from-gray-600 to-slate-600",
  "Entertainment": "from-purple-600 to-indigo-600",
  "Business & Networking": "from-blue-600 to-indigo-600",
  "Business & Productivity": "from-slate-600 to-gray-600",
  "Finance & Shopping": "from-emerald-500 to-teal-500"
};

// First letter icons for apps
const getAppInitial = (name: string) => {
  return name.charAt(0).toUpperCase();
};

export function MobileAppsSection() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [visibleCount, setVisibleCount] = useState(12);


  const filteredApps = selectedCategory === "All"
    ? mobileApps
    : mobileApps.filter(app => app.category === selectedCategory);

  // Alphabetical sorting
  filteredApps.sort((a, b) => a.name.localeCompare(b.name));

  const displayedApps = filteredApps.slice(0, visibleCount);

  const handleStoreClick = (appName: string, store: 'App Store' | 'Play Store') => {
    // Analytics tracking
    console.log(`[Analytics] Store click: ${appName} - ${store}`);
    // If window.gtag exists, we could use it here
  };

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Smartphone className="w-5 h-5 text-primary" />
            <span className="text-primary font-semibold tracking-wider uppercase text-sm">
              Live Applications
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Mobile Apps Portfolio
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Explore our collection of live mobile applications available on Google Play Store and Apple App Store
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {appCategories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setSelectedCategory(category);
                setVisibleCount(12);
              }}
              className="rounded-full"
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Apps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {displayedApps.map((app) => {
            const gradientClass = categoryColors[app.category] || "from-primary to-primary/80";
            return (

              <div key={app.name} className="block h-full">
                <TiltCard className="group glass-card-hover rounded-xl p-6 transition-all duration-300 h-full flex flex-col items-stretch relative">
                  <a href={app.storeUrl} target="_blank" rel="noopener noreferrer" className="block flex-1 group">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        {/* App Icon with gradient and initial */}
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradientClass}
                                      flex items-center justify-center flex-shrink-0 shadow-lg
                                      group-hover:scale-110 transition-transform duration-300`}>
                          <span className="text-white font-bold text-xl">
                            {getAppInitial(app.name)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors truncate pr-2">
                            {app.name}
                          </h3>
                          <span className="text-xs text-primary/80 font-medium block truncate">{app.category}</span>
                        </div>
                      </div>

                      {/* Stats Badge - Now in flex flow */}
                      {app.stats && (
                        <span className="bg-primary/10 text-primary text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider backdrop-blur-sm border border-primary/20 whitespace-nowrap flex-shrink-0 mt-1">
                          {app.stats}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {app.description}
                    </p>
                  </a>

                  <div className="flex items-center justify-between mt-auto pt-2 border-t border-border/50">
                    {app.platform === 'both' ? (
                      <div className="flex gap-2 w-full">
                        <a
                          href={app.storeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleStoreClick(app.name, 'Play Store')}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-lg font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                        >
                          <SiGoogleplay aria-hidden="true" className="w-3.5 h-3.5" />
                          <span>Play Store</span>
                        </a>
                        <a
                          href={app.secondaryStoreUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleStoreClick(app.name, 'App Store')}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-lg font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        >
                          <SiAppstore aria-hidden="true" className="w-3.5 h-3.5" />
                          <span>App Store</span>
                        </a>
                      </div>
                    ) : (
                      <a
                        href={app.storeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleStoreClick(app.name, app.platform === 'ios' ? 'App Store' : 'Play Store')}
                        className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition-colors
                          ${app.platform === 'ios'
                            ? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                            : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                          }`}
                      >
                        {app.platform === 'ios' ? (
                          <SiAppstore aria-hidden="true" className="w-3.5 h-3.5" />
                        ) : (
                          <SiGoogleplay aria-hidden="true" className="w-3.5 h-3.5" />
                        )}
                        {app.platform === 'ios' ? 'App Store' : 'Play Store'}
                      </a>
                    )}

                    {app.platform !== 'both' && (
                      <a href={app.storeUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4 text-muted-foreground hover:text-primary transition-colors" />
                      </a>
                    )}
                  </div>
                </TiltCard>
              </div>
            );

          })}
        </div>

        {/* Load More */}
        {visibleCount < filteredApps.length && (
          <div className="text-center mt-10">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setVisibleCount(prev => prev + 8)}
            >
              Load More Apps
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
